import { useAddBetrokkenStations, useGetDossierBetrokkenStationsById } from "@/entities/betrokken-stations";
import { Station } from "@/entities/station";
import { StationMultiSelect } from "@/features/infra";
import {
    Button,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Form,
} from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { RadixBaseUiInteropContextProvider } from "@/shared/components/utils/radix-base-ui-interop-provider";
import { useCallback, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

interface AddBetrokkenStationDialogProps {
    open: boolean;
    onClose: () => void;
    dossierNumber: string;
}

interface StationFormData extends Record<string, unknown> {
    stationCodes: string[];
}

export function AddBetrokkenStationDialog({ open, onClose, dossierNumber }: Readonly<AddBetrokkenStationDialogProps>) {
    const radixRef = useRef<HTMLDivElement | null>(null);

    const form = useForm<StationFormData>({
        defaultValues: {
            stationCodes: [],
        },
    });
    const { isPending, ...addBetrokkenStations } = useAddBetrokkenStations();
    const [submitError, setSubmitError] = useState<Error | null>(null);
    const selectedStationCodes = useWatch({ control: form.control, name: "stationCodes" }) ?? [];

    const { data: betrokkenStations } = useGetDossierBetrokkenStationsById(dossierNumber);

    // Only stations that are not betrokken yet are selectable, so the same station cannot be added
    // twice. Codes are compared lower-cased, matching how station codes are looked up elsewhere.
    const betrokkenStationCodes = useMemo(
        () => new Set(betrokkenStations?.map((station) => station.stationsCode.toLowerCase()) ?? []),
        [betrokkenStations]
    );

    const isNotBetrokkenYet = useCallback(
        (station: Station) => !betrokkenStationCodes.has(station.code.toLowerCase()),
        [betrokkenStationCodes]
    );

    const handleSubmit = async (data: StationFormData) => {
        if (data.stationCodes.length === 0) {
            return;
        }

        try {
            setSubmitError(null);
            await addBetrokkenStations.mutateAsync({ dossierNumber, stationCodes: data.stationCodes });

            form.reset();
            onClose();
        } catch (error) {
            setSubmitError(error as Error);
        }
    };

    const handleCancel = () => {
        form.reset();
        setSubmitError(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <RadixBaseUiInteropContextProvider radixElement={radixRef}>
                <DialogContainer className="max-w-md" ref={radixRef}>
                    <DialogHeader>
                        <DialogTitle>Betrokken stations handmatig toevoegen</DialogTitle>
                    </DialogHeader>

                    <Form {...form}>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                void form.handleSubmit(handleSubmit)(e);
                            }}
                        >
                            <DialogBody className="space-y-4">
                                <DialogDescription>
                                    Selecteer één of meerdere stations om handmatig toe te voegen aan dit dossier. De stations worden
                                    toegevoegd met betrokkenheid type "Handmatig".
                                </DialogDescription>
                                <StationMultiSelect
                                    form={form}
                                    name="stationCodes"
                                    label="Stations"
                                    placeholder="Selecteer stations..."
                                    nlStationsOnly
                                    filterFunction={isNotBetrokkenYet}
                                    data-testid="betrokken-stations-add-station"
                                />
                            </DialogBody>

                            <DialogFooter>
                                {submitError && <ErrorDisplay error={submitError} title="Fout bij het toevoegen van stations" />}
                                <div className="flex flex-row justify-end gap-2">
                                    <Button type="button" variant="secondary" onClick={handleCancel} disabled={isPending}>
                                        Annuleren
                                    </Button>
                                    <Button type="submit" variant="primary" disabled={isPending || selectedStationCodes.length === 0}>
                                        Toevoegen
                                    </Button>
                                </div>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContainer>
            </RadixBaseUiInteropContextProvider>
        </Dialog>
    );
}
